import { db } from '../config/firebase';
import {
  collection, doc, setDoc, getDoc, getDocs, query, where,
  Timestamp, onSnapshot, deleteDoc
} from 'firebase/firestore';
import {
  Schedule, TimeSlot, HourlyAvailability, DateAvailability, SlotAvailabilityMap
} from '../types/schedule.types';
import { format } from 'date-fns';

// =========================================================================
// 30-minute slot infrastructure (Phase 1)
// =========================================================================

// Default slot length, in minutes. All new slot generation uses this.
export const SLOT_MINUTES = 30;

// Convert minutes-since-midnight to "HH:mm".
const minutesToTimeStr = (m: number): string => {
  const h = Math.floor(m / 60);
  const min = m % 60;
  return `${h.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
};

// Convert "HH:mm" or numeric hour to minutes-since-midnight.
const keyToMinutes = (key: string): number => {
  if (key.includes(':')) {
    const [h, m] = key.split(':').map(Number);
    return h * 60 + (m || 0);
  }
  // Legacy numeric hour key
  return Number(key) * 60;
};

// Expand a (possibly legacy) availability map into a normalized
// SLOT_MINUTES-keyed map. A legacy `{ 9: true }` (whole hour) becomes
// `{ "09:00": true, "09:30": true }`. A new `{ "09:30": true }` passes through.
export const expandAvailabilityMap = (
  raw: SlotAvailabilityMap | undefined,
  slotMinutes: number = SLOT_MINUTES
): SlotAvailabilityMap => {
  if (!raw) return {};
  const out: SlotAvailabilityMap = {};
  for (const key of Object.keys(raw)) {
    if (!raw[key]) continue;
    if (key.includes(':')) {
      // Already a time-string key. Pass through.
      out[key] = true;
    } else {
      // Legacy whole-hour key — expand into N sub-slots covering that hour.
      const startMin = Number(key) * 60;
      const subSlots = Math.floor(60 / slotMinutes);
      for (let i = 0; i < subSlots; i++) {
        out[minutesToTimeStr(startMin + i * slotMinutes)] = true;
      }
    }
  }
  return out;
};

// Generate {startTime,endTime} time slots from a normalized availability map.
// Slots are sorted ascending. Adjacent enabled slots stay separate (we do not
// merge them — UI wants individual bookable cells).
export const generateTimeSlots = (
  availability: SlotAvailabilityMap | undefined,
  slotMinutes: number = SLOT_MINUTES
): { startTime: string; endTime: string }[] => {
  const expanded = expandAvailabilityMap(availability, slotMinutes);
  const startMinutes = Object.keys(expanded)
    .map(keyToMinutes)
    .sort((a, b) => a - b);
  return startMinutes.map(m => ({
    startTime: minutesToTimeStr(m),
    endTime: minutesToTimeStr(m + slotMinutes),
  }));
};

// Resolve the availability map for a given schedule + date, walking the
// priority chain: dateAvailability > hourlyAvailability > legacy timeSlots.
const resolveAvailabilityForDate = (
  schedule: Schedule,
  date: Date
): SlotAvailabilityMap => {
  const dateStr = format(date, 'yyyy-MM-dd');
  const dayOfWeek = date.getDay();

  if (schedule.dateAvailability && schedule.dateAvailability[dateStr]) {
    return schedule.dateAvailability[dateStr];
  }

  if (schedule.hourlyAvailability && schedule.hourlyAvailability[dayOfWeek]) {
    return schedule.hourlyAvailability[dayOfWeek];
  }

  // Legacy timeSlots / defaultSlots fallback. Convert to map.
  const timeSlots = schedule.timeSlots || schedule.defaultSlots || [];
  const daySlots = timeSlots.filter(slot => slot.dayOfWeek === dayOfWeek);
  const map: SlotAvailabilityMap = {};
  for (const slot of daySlots) {
    const startMin = keyToMinutes(slot.startTime);
    const endMin = keyToMinutes(slot.endTime);
    for (let m = startMin; m < endMin; m += SLOT_MINUTES) {
      map[minutesToTimeStr(m)] = true;
    }
  }
  return map;
};

// Get bookable {startTime,endTime} slots for a schedule on a specific date.
// This is the new canonical helper. Use it instead of getAvailableHoursForDate
// when you want sub-hour granularity.
export const getAvailableSlotsForDate = (
  schedule: Schedule,
  date: Date,
  slotMinutes: number = SLOT_MINUTES
): { startTime: string; endTime: string }[] => {
  return generateTimeSlots(resolveAvailabilityForDate(schedule, date), slotMinutes);
};

// =========================================================================
// Legacy helpers — kept for backwards compatibility. Now powered by the
// centralized generator. Anything calling these still works, but new code
// should prefer getAvailableSlotsForDate.
// =========================================================================

export const hourlyAvailabilityToTimeSlots = (
  hourlyAvail: HourlyAvailability,
  dayOfWeek: number
): TimeSlot[] => {
  return generateTimeSlots(hourlyAvail[dayOfWeek]).map(s => ({
    dayOfWeek,
    startTime: s.startTime,
    endTime: s.endTime,
    capacity: 1,
    isRecurring: true,
  }));
};

// Legacy: convert time slots back into hourly weekly availability.
// Still emits whole-hour keys to remain backwards-compatible with consumers
// that haven't been migrated.
export const timeSlotsToHourlyAvailability = (
  timeSlots: TimeSlot[]
): HourlyAvailability => {
  const availability: HourlyAvailability = {};
  for (let day = 0; day < 7; day++) availability[day] = {};

  for (const slot of timeSlots) {
    const startHour = parseInt(slot.startTime.split(':')[0]);
    const endHour = parseInt(slot.endTime.split(':')[0]);
    for (let hour = startHour; hour < endHour; hour++) {
      availability[slot.dayOfWeek][hour] = true;
    }
  }
  return availability;
};

export const dateAvailabilityToTimeSlots = (
  dateAvail: DateAvailability,
  dateStr: string
): TimeSlot[] => {
  const dayAvail = dateAvail[dateStr];
  if (!dayAvail) return [];
  const dayOfWeek = new Date(dateStr).getDay();
  return generateTimeSlots(dayAvail).map(s => ({
    dayOfWeek,
    startTime: s.startTime,
    endTime: s.endTime,
    capacity: 1,
    isRecurring: false,
  }));
};

// LEGACY: returns whole-hour numbers for callers that still expect hours.
// New callers should use getAvailableSlotsForDate instead.
// We derive this from the canonical slot list and keep only :00 starts so the
// old contract is preserved when slots are aligned to whole hours.
export const getAvailableHoursForDate = (
  schedule: Schedule,
  date: Date
): number[] => {
  const slots = getAvailableSlotsForDate(schedule, date);
  const hours = new Set<number>();
  for (const s of slots) {
    const [h, m] = s.startTime.split(':').map(Number);
    if (m === 0) hours.add(h);
  }
  return Array.from(hours).sort((a, b) => a - b);
};

export const scheduleService = {
  async saveSchedule(scheduleId: string, schedule: Partial<Schedule>): Promise<void> {
    console.log('Saving schedule:', { scheduleId, schedule: { ...schedule, updatedAt: Timestamp.now() } });
    const scheduleRef = doc(db, 'schedules', scheduleId);
    await setDoc(scheduleRef, {
      ...schedule,
      updatedAt: Timestamp.now()
    }, { merge: true });
  },

  async getSchedule(scheduleId: string): Promise<Schedule | null> {
    const scheduleDoc = await getDoc(doc(db, 'schedules', scheduleId));
    const data = scheduleDoc.exists() ? scheduleDoc.data() as Schedule : null;
    return data;
  },

  async getProjectSchedules(projectId: string): Promise<Schedule[]> {
    const schedulesQuery = query(
      collection(db, 'schedules'),
      where('projectId', '==', projectId)
    );
    const snapshot = await getDocs(schedulesQuery);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Schedule));
  },

  // Alias for getProjectSchedules
  async getSchedulesByProject(projectId: string): Promise<Schedule[]> {
    return this.getProjectSchedules(projectId);
  },

  async createSchedule(data: Omit<Schedule, 'id' | 'createdAt' | 'updatedAt'>): Promise<Schedule> {
    const scheduleRef = doc(collection(db, 'schedules'));
    const schedule: Schedule = {
      ...data,
      id: scheduleRef.id,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    } as Schedule;
    await setDoc(scheduleRef, schedule);
    return schedule;
  },

  async updateSchedule(scheduleId: string, updates: Partial<Schedule>): Promise<void> {
    const scheduleRef = doc(db, 'schedules', scheduleId);
    await setDoc(scheduleRef, {
      ...updates,
      updatedAt: Timestamp.now()
    }, { merge: true });
  },

  async getAvailableSlots(
    projectId: string,
    date: Date
  ): Promise<{ schedule: Schedule; slots: TimeSlot[] }[]> {
    const schedules = await this.getProjectSchedules(projectId);
    const dayOfWeek = date.getDay();
    
    const availableSchedules = schedules
      .filter(schedule => schedule.isActive)
      .map(schedule => {
        const daySlots = schedule.defaultSlots.filter(slot => 
          slot.dayOfWeek === dayOfWeek && slot.isRecurring
        );
        return { schedule, slots: daySlots };
      })
      .filter(item => item.slots.length > 0);
    
    return availableSchedules;
  },

  async clearSchedule(scheduleId: string): Promise<void> {
    const scheduleRef = doc(db, 'schedules', scheduleId);
    await deleteDoc(scheduleRef);
  },

  subscribeToSchedule(scheduleId: string, callback: (schedule: Schedule | null) => void) {
    const scheduleRef = doc(db, 'schedules', scheduleId);
    return onSnapshot(scheduleRef, (doc) => {
      callback(doc.exists() ? doc.data() as Schedule : null);
    });
  },

  async saveDateAvailability(
    userId: string,
    projectId: string,
    userName: string,
    dateAvailability: DateAvailability,
    schoolSegmentId?: string
  ): Promise<void> {
    // Find existing schedule (scoped to the same campus) or create new one.
    // A user can hold one schedule per campus plus one project-wide (untagged).
    const schedules = await this.getProjectSchedules(projectId);
    const seg = schoolSegmentId ?? null;
    let schedule = schedules.find(s => s.userId === userId && (s.schoolSegmentId ?? null) === seg);

    if (schedule) {
      // Merge with existing date availability (don't overwrite past dates)
      const existingDateAvail = schedule.dateAvailability || {};
      const mergedAvail = { ...existingDateAvail, ...dateAvailability };

      // Update existing schedule
      await this.updateSchedule(schedule.id!, {
        dateAvailability: mergedAvail,
        updatedAt: Timestamp.now()
      });
    } else {
      // Create new schedule
      await this.createSchedule({
        userId,
        userName,
        projectId,
        dateAvailability,
        isActive: true,
        ...(schoolSegmentId ? { schoolSegmentId } : {})
      });
    }
  },

  async saveHourlyAvailability(
    userId: string,
    projectId: string,
    userName: string,
    hourlyAvailability: HourlyAvailability
  ): Promise<void> {
    // Find existing schedule or create new one
    const schedules = await this.getProjectSchedules(projectId);
    let schedule = schedules.find(s => s.userId === userId);

    if (schedule) {
      // Update existing schedule
      await this.updateSchedule(schedule.id!, {
        hourlyAvailability,
        updatedAt: Timestamp.now()
      });
    } else {
      // Create new schedule
      await this.createSchedule({
        userId,
        userName,
        projectId,
        hourlyAvailability,
        isActive: true
      });
    }
  },

  async getUserScheduleForProject(userId: string, projectId: string, schoolSegmentId?: string): Promise<Schedule | null> {
    const schedules = await this.getProjectSchedules(projectId);
    const seg = schoolSegmentId ?? null;
    return schedules.find(s => s.userId === userId && (s.schoolSegmentId ?? null) === seg) || null;
  },

  getAvailableHoursForDate(schedule: Schedule, date: Date): number[] {
    return getAvailableHoursForDate(schedule, date);
  },

  getAvailableSlotsForDate(
    schedule: Schedule,
    date: Date
  ): { startTime: string; endTime: string }[] {
    return getAvailableSlotsForDate(schedule, date);
  }
};
